import { invokeMap } from 'lodash';

import { OwnedObject, cloneDeep } from 'gear';

import { Point, Matrix, Frame } from '../../geometry';

const { interpolate } = Point;

// engine @ Engine/Figure/TextChar

/* We use directly strings in Javascript, using PIVOT.xxx is not secure anyways
const PIVOT = {
  LXLY: 0,
  MXLY: 1,
  HXLY: 2,
  HXMY: 3,
  HXHY: 4,
  LXHY: 6,
  LXMY: 7,
  LXBY: 9,
  HXBY: 11,
  Grip: 12,
  BottomCenterGrip: 13,
  TopCenterGrip: 14,
  MiddleCenterGrip: 15,
  BaselineCenterGrip: 16,
  BaselineRightGrip: 17
};
*/

export const TextChar = OwnedObject.extend({ typeName: 'TextChar',

  Properties: {

    // Quick access to layout information
    lineIdx: { type: 'integer', def: 0 },
    idx: { type: 'integer', def: 0 },
    originalIdx: { type: 'integer', def: 0 },

    // Review, text processes sequences requires charData to not be owned
    charData: { type: 'TextCharData', def: null, owned: false },

    char: { type: 'char', def: null },
    bounds: { type: 'Bounds', def: null },
    grip: { type: 'Point', def: null },
    bottomCenterGrip: { type: 'Point', def: null },
    baselineCenterGrip: { type: 'Point', def: null },
    middleCenterGrip: { type: 'Point', def: null },
    topCenterGrip: { type: 'Point', def: null },
    baselineRightGrip: { type: 'Point', def: null },
    advanceX: { type: 'number', def: 0 },
    advanceY: { type: 'number', def: 0 },
    hPos: { type: 'number', def: 0 },
    originalHPos: { type: 'number', def: 0 }
  },

  init( config ) {
    this._outlines = config.outlines;
    this._frame = Frame.fromBounds(this._bounds);
  },

  clone() {
    // console.log('cloning TextChar');
    const c = new TextChar(this);
    c._frame = this._frame.clone();
    c._outlines = cloneDeep(this._outlines);
    return c;
  },

  // [TODO]
  bearingLX() {
    return this._bounds.lx - this._grip.x;
  },
  bearingHX() {
    return this._bounds.hx - this._grip.x;
  },
  bearingHY() {
    return this._bounds.hy - this._grip.y;
  },
  bearingLY() {
    return this._grip.y - this._bounds.ly;
  },

  computedSlantedAdvance( lineDirection) {
    const dx = Math.abs(lineDirection.x),
          dy = Math.abs(lineDirection.y),
          phi = Math.atan2(dy,dx);

    return lineDirection.scaled( dx >= dy ? this._advanceX / Math.cos(phi) : this._advanceY / Math.sin(phi) );
  },

  namedPoint(  name ) {
    const frame = this._frame;
    switch( name ) {
      case 'Grip'               : return this._grip;
      case 'BottomCenterGrip'   : return this._bottomCenterGrip;
      case 'BaselineCenterGrip' : return this._baselineCenterGrip;
      case 'TopCenterGrip'      : return this._topCenterGrip;
      case 'MiddleCenterGrip'   : return this._middleCenterGrip;
      case 'BaselineRightGrip'  : return this._baselineRightGrip;

      case 'LXBY'  : return this._grip .plus( frame.dir_x.sm( this.bearingLX() ) );
      case 'HXBY'  : return this._grip .plus( frame.dir_x.sm( this.bearingHX() ) );

      case 'LXLY'  : return frame.p0;
      case 'HXLY'  : return frame.p1;
      case 'HXMY'  : return interpolate(frame.p1,frame.p2,0.5);
      case 'HXHY'  : return frame.P2;
      case 'LXHY'  : return frame.p3;
      case 'LXMY'  : return interpolate(frame.p3,frame.p0,0.5);
    }
  },

  /* Maybe we do not need this?
  transform( t ) {
    this._grip.transform(t);

    const d = t.decompose();
    if( d.scaleX != 1.0 || d.scaleY != 1.0 ) {
      this._hPos     *= d.scaleX;
      this._advanceX *= d.scaleX;
      this._advanceY *= d.scaleY;
    }

    this.transformGripPoints(t);
    this.transformShape(t);
  },
  */

  transform( t ) {
    this.transformShape( t );
    this.transformGripPoints( t );
  },

  displace( dx,dy) {
    const t = Matrix.translate(dx, dy);
    this.transformShape( t );
    this.transformGripPoints( t );
  },

  placeAt( pivot,p) {
    const t = Matrix.translate( p.minus(this.namedPoint(pivot)) );
    this.transformShape( t );
    this.transformGripPoints( t );
  },

  scaleAround(  pivot, sX, sY ) {
    const t = Matrix.scaleFrom(this.namedPoint(pivot), sX, sY);
    this.transformShape( t ) ;
    this.transformGripPoints( t );

    this._advanceX *= sX ;
    this._advanceY *= sY ;
  },

  rotateAround(  pivot, phi ) {
    const t = Matrix.rotateAround(this.namedPoint(pivot), phi);
    this.transformShape( t );
    this.transformGripPoints( t );
  },

  mirrorAround(  pivot, h, v ) {
    this.scaleAround( pivot, h ? -1 : 1, v ? -1 : 1 );
  },

  transformGripPoints( t ) {
    this._grip.transform(t);
    this._bottomCenterGrip.transform(t);
    this._baselineCenterGrip.transform(t);
    this._middleCenterGrip.transform(t);
    this._topCenterGrip.transform(t);
    this._baselineRightGrip.transform(t);
  },

  transformShape( t ) {
    this._frame.transform(t);

    this._bounds = this._frame.bounds;

    const outlines = this._outlines;
    if( outlines ) {
      invokeMap(outlines,'transform',t);
    }
  },

  // Used by Deform processes (like FitOnEnvelope) so Text tool can track char positions
  transformBasicPoints( t ) {
    this._frame.transform(t);
    this._bounds = this._frame.bounds;
    this.transformGripPoints(t);
  }
});

TextChar.fromGlyph = function( glyph, lineIdx, idx, charData ) {

  const bounds = glyph._bounds,
        hPos = glyph._hPos,
        mx = bounds.mx;

  return new TextChar({
    lineIdx    : lineIdx,
    idx        : idx,
    originalIdx: glyph._originalIdx,
    charData   : charData,

    char     : glyph._char,
    outlines : cloneDeep(glyph._outlines),
    bounds   : bounds.clone(),
    advanceX : glyph._advanceX,
    advanceY : glyph._advanceY,
    hPos     : hPos,
    originalHPos: hPos,

    grip               : new Point( 0, 0 ),
    bottomCenterGrip   : new Point( mx, bounds.ly ),
    baselineCenterGrip : new Point( mx, 0 ),
    middleCenterGrip   : new Point( mx, bounds.my ),
    topCenterGrip      : new Point( mx, bounds.hy ),
    baselineRightGrip  : new Point( bounds.hx, 0 )
  });
};


// [TODO] This could be simplified

// engine @ Engine/Figure/TextCharTransformations
// We can represent this transformations with functors to simplify the code

TextChar.Transformer = OwnedObject.extend({ typeName: 'TextCharTransformer',
  init( t ) {
    this.t = t;
  },
  transform( char ) {
    char.transform(this.t);
  },
  clone() {
    return new TextChar.Transformer(this.t);
  }
});

TextChar.Displacer = OwnedObject.extend({ typeName: 'TextCharDisplacer',
  init( dx, dy ) {
    this.dx = dx;
    this.dy = dy;
  },
  transform( char ) {
    char.displace(this.dx,this.dy);
  },
  clone() {
    return new TextChar.Displacer(this.dx,this.dy);
  }
});

TextChar.TangencialDisplacer = OwnedObject.extend({ typeName: 'TextCharTangencialDisplacer',
  init( h, v ) {
    this.h = h;
    this.v = v;
  },
  transform( char ) {
    const h = this.h, v = this.v;
    const dirX = char._frame.dir_x;
    const offset = dirX.scaled(h) .plus( dirX.perpendicular().scaled(v) );
    char.displace(offset.x,offset.y);
  },
  clone() {
    return new TextChar.TangencialDisplacer(this.h,this.v);
  }
});

TextChar.PinPlacer = OwnedObject.extend({ typeName: 'TextCharPinPlacer',
  init( pivot, pos ) {
    this.pivot = pivot;
    this.pos = pos;
  },
  transform( char) {
    char.placeAt(this.pivot,this.pos);
  },
  clone() {
    return new TextChar.PinPlacer(this.pivot,this.pos);
  }
});

TextChar.PivotedScaler = OwnedObject.extend({ typeName: 'TextCharPivotedScaler',
  init( pivot, sx, sy ) {
    this.pivot = pivot;
    this.sx = sx;
    this.sy = sy;
  },
  transform( char) {
    char.scaleAround( this.pivot, this.sx, this.sy);
  },
  clone() {
    return new TextChar.PivotedScaler(this.pivot,this.sx,this.sy);
  }
});

TextChar.PivotedRotator = OwnedObject.extend({ typeName: 'TextCharPivotedRotator',
  init( pivot, phi ) {
    this.pivot = pivot;
    this.phi = phi;
  },
  transform( char ) {
    char.rotateAround(this.pivot,this.phi);
  },
  clone() {
    return new TextChar.PivotedRotator(this.pivot,this.phi);
  }
});

TextChar.PivotedMirrorer = OwnedObject.extend({ typeName: 'TextCharPivotedMirrorer',
  init( pivot, h, v ) {
    this.pivot = pivot;
    this.h = h;
    this.v = v;
  },
  transform( char) {
    char.mirrorAround(this.pivot,this.h,this.v);
  },
  clone() {
    return new TextChar.PivotedMirrorer(this.pivot,this.h,this.v);
  }
});



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/text/text-char.js
 **/